import React, { useState } from "react";
import { ChevronDown, FileText } from "lucide-react";

/* Collapsible section in the left toolbar. */
export const ToolSection = ({ icon: Icon, title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#D3D1C7]">
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-[#FAFAF9] transition-colors"
      >
        <Icon className="w-3.5 h-3.5 text-[#5F5E5A]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A] font-medium">
          {title}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 ml-auto text-[#5F5E5A] transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

export const PropField = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] mb-1">{label}</div>
    {children}
  </div>
);

export const IconBtn = ({ children, onClick, title }) => (
  <button
    onClick={onClick}
    title={title}
    className="p-1 rounded text-[#5F5E5A] hover:bg-[#F1EFE8] hover:text-[#2C2C2A]"
  >
    {children}
  </button>
);

export const ToggleBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
      active
        ? "bg-[#534AB7] text-white border-[#534AB7]"
        : "border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8]"
    }`}
  >
    {children}
  </button>
);

/* Inline blurb editor on the back-cover panel. Strips formatting on paste so
   users can paste blurbs straight from Word / Google Docs / web pages. */
export const BackBlurbSection = ({ onPlace, onOpenModal }) => {
  const [text, setText] = useState("");
  const handlePaste = (e) => {
    const dt = e.clipboardData;
    const html = dt?.getData("text/html");
    if (html) {
      e.preventDefault();
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      tmp.querySelectorAll("script, style, head, meta, link, noscript").forEach((el) => el.remove());
      tmp.querySelectorAll("p, div, br, li, h1, h2, h3, h4, h5, h6").forEach((el) => el.append("\n"));
      const decoded = (tmp.textContent || "")
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+/g, " ")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      const ta = e.currentTarget;
      const start = ta.selectionStart || 0;
      const end = ta.selectionEnd || 0;
      const next = text.slice(0, start) + decoded + text.slice(end);
      setText(next);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + decoded.length; }, 0);
    }
  };
  const handlePlace = () => {
    if (!text.trim()) return;
    onPlace(text.trim());
  };
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  return (
    <div className="border-b border-[#D3D1C7] bg-[#F1EFE8]/40" data-testid="back-blurb-section">
      <div className="px-4 py-3 flex items-center gap-2">
        <FileText className="w-3.5 h-3.5 text-[#534AB7]" strokeWidth={1.6} />
        <span className="text-xs uppercase tracking-[0.18em] text-[#534AB7] font-medium">Blurb</span>
      </div>
      <div className="px-4 pb-4 space-y-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          rows={6}
          placeholder={`Paste or type your back-cover blurb here.\n\nPaste from Word, Google Docs, or a webpage — formatting is stripped automatically.`}
          data-testid="back-blurb-textarea"
          className="w-full px-2.5 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-xs text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] resize-none leading-relaxed"
        />
        <div className="flex items-center justify-between text-[10px] text-[#5F5E5A]">
          <span>{wordCount} words · {text.length} chars</span>
          <button
            onClick={onOpenModal}
            className="underline-offset-2 hover:underline text-[#534AB7]"
            data-testid="open-blurb-advanced-btn"
          >
            Or import from URL →
          </button>
        </div>
        <button
          onClick={handlePlace}
          disabled={!text.trim()}
          data-testid="place-blurb-btn"
          className="w-full px-3 py-2 rounded-[8px] bg-[#534AB7] text-white text-xs font-medium hover:bg-[#3C3489] disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          <FileText className="w-3 h-3" /> Place blurb on canvas
        </button>
      </div>
    </div>
  );
};
